import type { EventSourceFetchInit } from 'eventsource';
import type {
  XSdkNotifyConstructorOptions,
  XSdkNotifyData,
  XSdkNotifyBroadcastData,
  XSdkNotifyPrivateData,
  XSdkNotifyClientData,
  XSdkNotifyRemoteData,
  XSdkNotifyEventSourceEventMap,
  XSdkNotifyClientSignal,
} from '../typings';
import { EventSource } from 'eventsource';
import { omit, uid } from 'radash';
import {
  INIT_SIGNAL,
  DEAD_SIGNAL,
  EXCLUDE_SIGNAL,
  NOTIFY_SIGNAL_LIST,
  CLIENT_SIGNAL_PREFIX_SYMBOL,
} from '../typings';
import { getBaseConfigByOrigin } from '../utils/config';

function useFetchLike(token: () => string, url: string | URL, init: EventSourceFetchInit) {
  const _token = token?.()?.replace(/^(bearer ?)?/, 'bearer ');
  const headers = omit(init.headers, ['last-event-id', 'Last-Event-Id']);

  if (_token) {
    Object.assign(headers, { Authorization: _token });
  }

  return fetch(url, { ...init, headers });
}

/**
 * 华龙芯全媒体采编中台体系下，前端的多窗口事件广播服务类，及后端 SSE 事件通信服务类。其内部集成功能如下：
 *
 * - 集成华龙芯后端的 SSE 消息通知服务
 * - 多窗口共享通知
 * - 多窗口连接唯一
 */
export class NotifySDK extends EventTarget {
  /**
   * 向后端发起 SSE 连接的接口服务地址（已归一化为协议相对地址）
   *
   * @private
   */
  private readonly _apiBase: string;

  /**
   * 向后端发起连接请求时须携带的授权令牌
   *
   * @private
   */
  private readonly _token: () => string;

  /**
   * 授权令牌过期时的自动刷新策略
   *
   * @private
   */
  private readonly _freshToken: () => Promise<void>;

  /**
   * 事件过滤器，过滤非通知类消息事件
   *
   * @private
   */
  private readonly _eventFilter: (str: string) => boolean = (str) => ['HEARTBEAT'].includes(str);

  /**
   * 实例ID
   */
  private readonly _uid = uid(16);

  /**
   * 初始化时间
   *
   * @defaultValue Date.now()
   */
  private _time = Date.now();

  /**
   * 此 sdk 实例是否已经参与竞选收发实例，并失败
   */
  private _excluded = false;

  /**
   * SSE 连接等待 clearTimeout 的 id
   */
  private _delay: number;

  /**
   * 此 sdk 实例是否为消息收发实例
   */
  private _isReceiver: boolean | undefined;

  /**
   * Eventsource
   */
  private _eventsource: EventSource;

  /**
   * BroadcastChannel
   */
  private _broadcastChannel: BroadcastChannel = new BroadcastChannel(
    'HLX_NOTIFY_BROADCAST_CHANNEL',
  );

  constructor(options: XSdkNotifyConstructorOptions) {
    super();
    this._apiBase = getBaseConfigByOrigin(options.apiBase ?? '');

    if (options.token) {
      this._token = options.token;
    }

    if (options.freshToken) {
      this._freshToken = options.freshToken;
    }

    if (options.eventFilter) {
      if (typeof options.eventFilter === 'function') {
        this._eventFilter = options.eventFilter;
      } else if (Array.isArray(options.eventFilter)) {
        this._eventFilter = (str: string) => (options.eventFilter as string[]).includes(str);
      }
    }

    // 接收来自其他窗口的广播
    this._broadcastChannel.addEventListener(
      'message',
      (evt: MessageEvent<XSdkNotifyPrivateData>) => {
        const { type, uid, data } = evt.data;
        if (NOTIFY_SIGNAL_LIST.has(type) && uid) {
          this._localHandler(evt.data as XSdkNotifyPrivateData);
        } else if (type.startsWith(CLIENT_SIGNAL_PREFIX_SYMBOL)) {
          this.dispatchEvent(new MessageEvent(type, { data: data }));
        } else {
          this.dispatchEvent(new MessageEvent(type, { data: data, lastEventId: data.id }));
        }
      },
    );

    this._init();

    window.addEventListener('beforeunload', () => {
      //  SSE连接窗口关闭的时候需要要发出信号
      if (this._isReceiver) {
        this._broadcastChannel.postMessage({ type: DEAD_SIGNAL, uid: this._uid });
        this._closeConnect();
      }
      this._broadcastChannel.close();
    });
  }

  /**
   * 发起竞选信号
   */
  private _init() {
    if (!this._excluded) {
      this._broadcastChannel.postMessage({
        type: INIT_SIGNAL,
        uid: this._uid,
        data: this._time,
      });
      this._connectWait();
    }
  }

  private _resetInit() {
    this._excluded = false;
    this._isReceiver = false;
    this._init();
  }

  private _localHandler({ type, uid, data }: XSdkNotifyPrivateData) {
    if (type === INIT_SIGNAL) {
      if (this._isReceiver) {
        this._broadcastChannel.postMessage({
          type: EXCLUDE_SIGNAL,
          uid: uid,
          data: this._time,
        });
        return;
      }

      if (!this._excluded) {
        // 其他窗口的 初始化信号 需要重新等待连接
        this._connectWait();
        this._compare(data as unknown as number, uid);
      }

      return;
    }

    if (type === EXCLUDE_SIGNAL && this._uid === uid) {
      // 自己被其他窗口干掉了
      !this._excluded && this._exclude();
      return;
    }

    if (type === DEAD_SIGNAL) {
      // SSE 连接窗口死掉，准备重新竞争连接 SSE
      this._resetInit();
    }
  }
  /**
   * 毕业
   */
  private _exclude() {
    this._excluded = true;
    this._isReceiver = false;
    this._delay && clearTimeout(this._delay);
    this._eventsource = null;
  }
  /**
   * 竞争上岗
   */
  private _compare(otherSDKInitTime: number, uid: string) {
    if (this._isReceiver || this._time < otherSDKInitTime) {
      // 排除广播来源SDK
      return this._broadcastChannel.postMessage({
        type: EXCLUDE_SIGNAL,
        uid: uid,
        data: this._time,
      });
    }

    if (this._time > otherSDKInitTime) {
      // 排除自己
      this._exclude();
      return;
    }

    if (this._time === otherSDKInitTime) {
      // 两个窗口的初始化时间一致，虽然基本不可能
      // 需要重新加上随机数再次进行比较
      this._time += Math.floor(Math.random() * 1000);
      this._resetInit();
    }
  }
  /**
   * broadcast 本地握手完成 与 SSE连接 之间的缓冲等待时间
   */
  private _connectWait() {
    if (this._isReceiver) {
      return;
    }

    this._delay && clearTimeout(this._delay);
    this._delay = window.setTimeout(() => {
      this._connect();
    }, 2000);
  }

  /**
   * broadcast 本地握手完成，准备SSE连接
   */
  private _connect() {
    this._isReceiver = true;
    this._eventsource = new EventSource(
      `${this._apiBase}/notify-center-service/notify/connect?notifyChannel=WEB`,
      { fetch: (url, init) => useFetchLike(this._token, url, init) },
    );

    this._eventsource.addEventListener('open', () => {
      this.dispatchEvent(new Event('open'));
    });

    this._eventsource.addEventListener('error', async (e) => {
      if (e.code === 401 && !!this._freshToken) {
        try {
          await this._freshToken();
          setTimeout(() => {
            this._connect();
          }, 3000);
        } catch {
          this.dispatchEvent(new ErrorEvent('error', { message: e.message }));
        }
      } else {
        return this.dispatchEvent(new ErrorEvent('error', { message: e.message }));
      }
    });

    this._eventsource.addEventListener('message', (evt: MessageEvent<string>) => {
      const remoteData = JSON.parse(evt.data) as XSdkNotifyRemoteData;
      const msgType = remoteData.eventTypeCode || 'message';
      if (this._eventFilter?.(msgType)) {
        return;
      }

      const data: XSdkNotifyData = {
        id: evt.lastEventId,
        ...remoteData,
        eventTypeCode: msgType,
        eventSource: 'INTERFACE',
        eventOriginData: remoteData,
      };

      // 所有的消息都会触发 'message' 事件
      this.dispatchEvent(new MessageEvent('message', { data: data, lastEventId: data.id }));
      this._broadcastChannel.postMessage({
        type: 'message',
        data: { ...data, eventSource: 'BROADCAST' } as XSdkNotifyBroadcastData,
      });

      if (msgType !== 'message') {
        this.dispatchEvent(new MessageEvent(msgType, { data: data, lastEventId: data.id }));
        this._broadcastChannel.postMessage({
          type: msgType,
          data: { ...data, eventSource: 'BROADCAST' } as XSdkNotifyBroadcastData,
        });
      }
    });
  }

  /**
   * 断开SSE连接
   */
  private _closeConnect() {
    if (this._eventsource && !this._eventsource.CLOSED) {
      this._eventsource.close();
    }
  }

  /**
   * 注册 SDK 的生命周期事件监听器
   *
   * @param type - 要监听的事件类型
   *
   * @param listener - 事件回调
   *
   * @param options - 监听器配置
   */
  override addEventListener<K extends keyof XSdkNotifyEventSourceEventMap>(
    type: K,
    listener: (this: NotifySDK, event: XSdkNotifyEventSourceEventMap[K]) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;

  override addEventListener(
    type: 'message',
    listener: (this: NotifySDK, event: MessageEvent<XSdkNotifyData>) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;

  override addEventListener(
    type: XSdkNotifyClientSignal,
    listener: (this: NotifySDK, event: MessageEvent<XSdkNotifyClientData>) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;

  override addEventListener(
    type: string,
    listener: (this: NotifySDK, event: MessageEvent<XSdkNotifyData>) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;

  override addEventListener(
    type: string,
    listener:
      | ((this: NotifySDK, event: MessageEvent<XSdkNotifyClientData>) => unknown)
      | ((this: NotifySDK, event: MessageEvent<XSdkNotifyData>) => unknown)
      | EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void {
    const listen = listener as (this: EventSource, event: Event) => unknown;
    super.addEventListener(type, listen, options);
  }

  /**
   * 移除 SDK 的生命周期事件监听器
   *
   * @param type - 要监听的事件类型
   *
   * @param listener - 事件回调
   *
   * @param options - 监听器配置
   */
  override removeEventListener<K extends keyof XSdkNotifyEventSourceEventMap>(
    type: K,
    listener: (this: NotifySDK, event: XSdkNotifyEventSourceEventMap[K]) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;

  override removeEventListener(
    type: 'message',
    listener: (this: NotifySDK, event: MessageEvent<XSdkNotifyData>) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;

  override removeEventListener(
    type: XSdkNotifyClientSignal,
    listener: (this: NotifySDK, event: MessageEvent<XSdkNotifyClientData>) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;

  override removeEventListener(
    type: string,
    listener: (this: NotifySDK, event: MessageEvent<XSdkNotifyData>) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;

  override removeEventListener(
    type: string,
    listener:
      | ((this: NotifySDK, event: MessageEvent<XSdkNotifyData>) => unknown)
      | ((this: NotifySDK, event: MessageEvent<XSdkNotifyClientData>) => unknown)
      | EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void {
    const listen = listener as (this: EventSource, event: Event) => unknown;
    super.removeEventListener(type, listen, options);
  }

  public dispatchEvent(
    event: MessageEvent<XSdkNotifyData> | MessageEvent<XSdkNotifyClientData> | Event | ErrorEvent,
  ): boolean {
    return super.dispatchEvent(event as Event);
  }

  /**
   * 客户端给其他窗口发送消息
   *
   * @param message - 消息参数
   *
   * @param message.type - 信号标识，以 "CLIENT_" 作为前缀标识
   *
   * @param message.data - 消息内容
   */
  public postMessage(message: XSdkNotifyClientData) {
    if (!message.type.startsWith(CLIENT_SIGNAL_PREFIX_SYMBOL)) {
      throw new Error(
        `Client messages must be identified by the prefix "${CLIENT_SIGNAL_PREFIX_SYMBOL}".`,
      );
    }
    this._broadcastChannel.postMessage(message);
  }
}
